import { inject, NgZone,Service } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { ActionPerformed, PushNotifications, PushNotificationSchema, Token } from '@capacitor/push-notifications';
import { Platform } from '@ionic/angular/lazy';
import { filter, firstValueFrom, Observable, timeout } from 'rxjs';

import { NotificationType } from '@/app/app/constants/notification.enum';
import { PushPlatform } from '@/app/app/constants/push-platform.enum';
import { PushNotificationData } from '@/app/app/interfaces/app/push-notification-data';
import { UsersApi } from '@/app/app/services/api/users.api';
import { AppNavigationService } from '@/app/app/services/app/app-navigation.service';
import { NotificationService } from '@/app/app/services/communications/notification.service';
import { isNotNullOrEmpty } from '@/shared/lib/core/utils/data-validation.util';
import { MessageService } from '@/shared/lib/services/communications/message.service';
import { DataStore } from '@/shared/lib/stores/data.store';
import { DeploymentStore } from '@/shared/lib/stores/deployment.store';
import { UserStore } from '@/shared/lib/stores/user.store';

export const DEFAULT_PUSH_CHANNEL_ID = 'cloudotdr_default';

const STORE_LOAD_TIMEOUT_MS = 15000;

@Service()
export class PushNotificationService {
  zone = inject(NgZone);
  router = inject(Router);
  platform = inject(Platform);
  message = inject(MessageService);
  dataStore = inject(DataStore);
  deploymentStore = inject(DeploymentStore);
  userStore = inject(UserStore);
  notificationService = inject(NotificationService);
  usersApi = inject(UsersApi);
  appNavigation = inject(AppNavigationService);

  private isInitialized = false;
  private isRegistered = false;
  private tenantList$ = toObservable(this.deploymentStore.tenantList);
  private deploymentList$ = toObservable(this.deploymentStore.deploymentList);

  public async initialize(): Promise<void> {
    if (this.isInitialized || !Capacitor.isPluginAvailable('PushNotifications')) {
      return;
    }
    this.isInitialized = true;

    // Listeners must be attached before register(), otherwise the token can be missed.
    await this.addListeners();
    await this.createDefaultChannel();

    let permission = await PushNotifications.checkPermissions();
    if (permission.receive === 'prompt' || permission.receive === 'prompt-with-rationale') {
      permission = await PushNotifications.requestPermissions();
    }

    if (permission.receive !== 'granted') {
      this.message.warn('PushNotificationService.initialize()', 'Push notification permission not granted');
      return;
    }

    // Delivered notifications can only be managed once the device token arrived (iOS rejects earlier calls).
    this.platform.resume.subscribe(() => this.clearDeliveredNotifications());

    await PushNotifications.register();
  }

  private async addListeners(): Promise<void> {
    await PushNotifications.addListener('registration', (token: Token) => {
      this.zone.run(() => {
        this.message.add('PushNotificationService.registration', token.value);
        this.dataStore.setPushToken(token.value);
        this.registerPushNotificationReference(token.value);
        this.isRegistered = true;
        this.clearDeliveredNotifications();
      });
    });

    await PushNotifications.addListener('registrationError', (error: any) => {
      this.message.error('PushNotificationService.registrationError', error);
    });

    // Only fires while the app is in the foreground; background notifications are shown by the OS.
    await PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
      this.zone.run(() => {
        const text = [notification.title, notification.body].filter(isNotNullOrEmpty).join('\n');
        if (isNotNullOrEmpty(text)) {
          this.notificationService.presentToast(text, NotificationType.Success);
        }
      });
    });

    // Retained by the plugin until a listener is attached, so taps that cold-start the app arrive here too.
    await PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
      this.zone.run(() => this.openNotification(action.notification.data ?? {}));
    });
  }

  private async registerPushNotificationReference(pushNotificationToken: string): Promise<void> {
    // The device model is not unique across users' devices, so identify this install by its device id.
    const deviceRef = this.dataStore.deviceId() ?? (await Device.getId()).identifier;
    const userPushNotificationReference = {
      userId: this.userStore.userProfile()?.sub ?? 'unknownUserId',
      pushNotificationToken,
      deviceRef,
      // iOS delivers an APNs token, Android an FCM token
      platform: Capacitor.getPlatform() === 'ios' ? PushPlatform.Apple : PushPlatform.Android,
    };

    this.zone.run(() => {
      this.usersApi.registerPushNotificationReference(userPushNotificationReference).subscribe({
        next: () => {
          this.message.add('PushNotificationService.registerPushNotificationReference', 'success');
          this.dataStore.setPushTokenRegistered(true);
        },
        error: (error) => this.message.warn('PushNotificationService.registerPushNotificationReference', error),
      });
    });
  }

  private async openNotification(data: PushNotificationData): Promise<void> {
    const tenantId = this.toId(data.tenantId);
    const deploymentId = this.toId(data.deploymentId);

    let switched = false;
    try {
      if (tenantId !== null && tenantId !== this.deploymentStore.selectedTenantId()) {
        await this.waitFor(this.tenantList$, list => list?.some(t => t.id === tenantId) ?? false);
        this.deploymentStore.setTenant(tenantId, false);
        switched = true;
      }

      if (deploymentId !== null && deploymentId !== this.deploymentStore.selectedDeploymentId()) {
        await this.waitFor(this.deploymentList$, list => list?.some(d => d.id === deploymentId) ?? false);
        this.deploymentStore.setDeployment(deploymentId, false);
        switched = true;
      }
    } catch (error) {
      this.message.warn('PushNotificationService.openNotification()', error);
    }

    // Switched tenant/deployment: close the pages of the previous one before opening the notification's route
    if (switched) {
      await this.appNavigation.resetToHome();
    }

    // Only follow app-internal routes.
    if (typeof data.route === 'string' && data.route.startsWith('/') && !data.route.startsWith('//')) {
      this.router.navigateByUrl(data.route);
    }
  }

  private waitFor<T>(source$: Observable<T>, predicate: (value: T) => boolean): Promise<T> {
    return firstValueFrom(source$.pipe(filter(predicate), timeout(STORE_LOAD_TIMEOUT_MS)));
  }

  private toId(value: string | number | undefined): number | null {
    const id = Number(value);
    return isNotNullOrEmpty(value) && Number.isInteger(id) ? id : null;
  }

  private clearDeliveredNotifications(): void {
    if (!this.isRegistered) {
      return;
    }

    PushNotifications.removeAllDeliveredNotifications().catch(error =>
      this.message.warn('PushNotificationService.clearDeliveredNotifications()', error),
    );
  }

  private async createDefaultChannel(): Promise<void> {
    if (Capacitor.getPlatform() !== 'android') {
      return;
    }

    await PushNotifications.createChannel({
      id: DEFAULT_PUSH_CHANNEL_ID,
      name: 'cloudOTDR',
      description: 'cloudOTDR notifications',
      importance: 4,
      visibility: 1,
    });
  }
}
